// ============================================
// TTS 플레이어 모듈 (ES6)
// Web Audio API 기반 재생 (자동재생 정책 우회)
// ============================================

import { setIsPlaying } from './state.js';

// 오디오 큐 (base64 데이터 URL 저장)
let audioQueue = [];

// Web Audio API 컨텍스트 (전역 - 한 번 unlock하면 계속 사용)
let audioContext = null;

// 현재 재생 중인 소스
let currentSource = null;

// 볼륨
let volume = 1.0;
let gainNode = null;

// ============================================
// AudioContext 초기화 및 Unlock
// ============================================

/**
 * AudioContext 가져오기 (없으면 생성)
 */
function getAudioContext() {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioContext = new AudioContextClass();
      // GainNode 생성 (볼륨 제어용)
      gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);
      gainNode.gain.value = volume;
    }
  }
  return audioContext;
}

/**
 * 사용자 인터랙션 시 호출하여 오디오 재생 권한 획득
 * AudioContext를 resume하여 자동재생 정책 우회
 */
export function unlockAudio() {
  const ctx = getAudioContext();
  if (!ctx) return Promise.resolve();

  if (ctx.state === 'suspended') {
    return ctx.resume().then(() => {
      console.log('[TTS Player] AudioContext unlocked');
    }).catch(() => {
      // 실패해도 계속 진행
    });
  }
  return Promise.resolve();
}

export function isAudioUnlocked() {
  return audioContext && audioContext.state === 'running';
}

// ============================================
// 큐 관리
// ============================================

export function getAudioQueue() {
  return audioQueue;
}

export function clearAudioQueue() {
  audioQueue = [];
}

// ============================================
// Base64 데이터 URL을 ArrayBuffer로 변환
// ============================================

async function dataUrlToArrayBuffer(dataUrl) {
  // data:audio/wav;base64,XXXX 형식에서 base64 부분 추출
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ============================================
// 재생 제어
// ============================================

/**
 * 오디오 데이터 URL 재생 (Web Audio API)
 * @param {string} audioDataUrl - base64 데이터 URL
 * @returns {Promise}
 */
export async function playAudioFromDataUrl(audioDataUrl) {
  const ctx = getAudioContext();
  if (!ctx) {
    console.error('[TTS Player] AudioContext 지원 안됨');
    return;
  }

  // Suspended 상태면 resume 시도
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch (e) {
      console.warn('[TTS Player] AudioContext resume 실패:', e);
    }
  }

  // 이전 재생 중지
  if (currentSource) {
    try {
      currentSource.stop();
    } catch (e) {
      // 이미 중지됨
    }
    currentSource = null;
  }

  try {
    // Base64 → ArrayBuffer
    const arrayBuffer = await dataUrlToArrayBuffer(audioDataUrl);

    // ArrayBuffer → AudioBuffer
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

    // BufferSource 생성 및 연결
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(gainNode);

    currentSource = source;

    return new Promise((resolve) => {
      source.onended = () => {
        currentSource = null;
        resolve();
      };
      source.start(0);
    });
  } catch (error) {
    console.error('[TTS Player] 재생 실패:', error);
    throw error;
  }
}

/**
 * 레거시 호환 - Audio 엘리먼트 재생 (Web Audio API로 대체)
 */
export function playAudio(audio) {
  // Audio 엘리먼트의 src가 data URL인 경우 Web Audio API로 재생
  if (audio.src && audio.src.startsWith('data:')) {
    return playAudioFromDataUrl(audio.src);
  }

  // 그 외의 경우 기존 방식 사용
  if (currentSource) {
    try {
      currentSource.stop();
    } catch (e) {}
    currentSource = null;
  }

  return audio.play().catch(error => {
    console.error('[TTS Player] 재생 실패:', error);
  });
}

/**
 * 현재 재생 일시정지
 */
export function pauseAudio() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'running') {
    ctx.suspend();
    setIsPlaying(false);
  }
}

/**
 * 재생 재개
 */
export function resumeAudio() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
    setIsPlaying(true);
  }
}

/**
 * 모든 재생 중지 및 큐 클리어
 */
export function stopAllAudio() {
  // 현재 재생 중지
  if (currentSource) {
    try {
      currentSource.stop();
    } catch (e) {}
    currentSource = null;
  }

  // 큐 비우기
  audioQueue = [];
  setIsPlaying(false);
}

/**
 * 현재 재생 중인 오디오 가져오기 (레거시 호환)
 */
export function getCurrentAudio() {
  return currentSource;
}

// ============================================
// 볼륨 제어
// ============================================

export function setVolume(value) {
  volume = Math.max(0, Math.min(1, value));
  if (gainNode) {
    gainNode.gain.value = volume;
  }
}

export function getVolume() {
  return volume;
}

// ============================================
// 재생 상태 확인
// ============================================

export function isCurrentlyPlaying() {
  return currentSource !== null;
}

export function getQueueLength() {
  return audioQueue.length;
}
